/**
 * Reading a metric into a component.
 *
 * Refresh is 15s and deliberately not faster: the server caches shared results, and a tighter
 * interval would spend the session's polling budget without producing newer numbers.
 *
 * A failed refresh does NOT clear the last good value — it marks the reading unavailable and
 * says why. Blanking the screen on one bad poll is how a transient hiccup reads as an outage.
 */
import { useEffect, useState } from 'react';
import { getMetric, type ApiFailure, type MetricResult } from './api';

export interface MetricState {
  result: MetricResult | null;
  failure: ApiFailure | null;
  loading: boolean;
}

export function useMetric(id: string, intervalMs = 15_000): MetricState {
  const [state, setState] = useState<MetricState>({ result: null, failure: null, loading: true });

  useEffect(() => {
    let alive = true;
    async function tick() {
      const r = await getMetric(id);
      if (!alive) return;
      setState((prev) => r.ok
        ? { result: r.data, failure: null, loading: false }
        // Keep the previous reading so the UI can show it as stale rather than empty.
        : { result: prev.result, failure: r.failure, loading: false });
    }
    void tick();
    const t = setInterval(() => void tick(), intervalMs);
    return () => { alive = false; clearInterval(t); };
  }, [id, intervalMs]);

  return state;
}

/** Sum every sample — for a metric whose total is the meaningful figure. */
export function total(m: MetricResult | null): number | undefined {
  if (!m || m.samples.length === 0) return undefined;
  return m.samples.reduce((a, s) => a + s.value, 0);
}

/** Largest sample, with its labels — for "worst node", "fullest volume". */
export function peak(m: MetricResult | null): { value: number; labels: Record<string, string> } | undefined {
  if (!m || m.samples.length === 0) return undefined;
  return m.samples.reduce((a, s) => (s.value > a.value ? s : a), m.samples[0]!);
}
